import { useImageProcessing } from "@/features/draw/composables/useImageProcessing";
import {
  CANVAS_SIZE,
  getPixelSizeForCanvas,
} from "@/features/draw/constants/canvas";
import type { CanvasRenderingOptions } from "@/features/draw/types";
import { intToHex, intToRgb } from "@/features/draw/utils/color/colorUtils";
import { usePreviewImg } from "@/use/common";
import { showImagePreview } from "vant";

export function renderCanvas(
  ctx: CanvasRenderingContext2D,
  options: CanvasRenderingOptions
) {
  const { width, height, pixelSize, pixels } = options;

  // Create ImageData for efficient pixel manipulation
  const imageData = ctx.getImageData(
    0,
    0,
    width * pixelSize,
    height * pixelSize
  );
  const data = imageData.data;

  // Create color cache for better performance
  const colorCache = new Map<number, { r: number; g: number; b: number }>();

  // Fill pixel data
  pixels.forEach((color: number, index: number) => {
    const x = (index % width) * pixelSize;
    const y = Math.floor(index / width) * pixelSize;

    // Fill the pixel area
    for (let py = 0; py < pixelSize; py++) {
      const rowOffset = ((y + py) * width * pixelSize + x) * 4;
      for (let px = 0; px < pixelSize; px++) {
        const pixelIndex = rowOffset + px * 4;

        if (color === 0) {
          // Set transparent pixel
          data[pixelIndex + 3] = 0;
        } else {
          // Get RGB values from cache or parse color
          let rgb = colorCache.get(color);
          if (!rgb) {
            rgb = intToRgb(color);
            colorCache.set(color, rgb);
          }

          // Set pixel color
          data[pixelIndex] = rgb.r;
          data[pixelIndex + 1] = rgb.g;
          data[pixelIndex + 2] = rgb.b;
          data[pixelIndex + 3] = 255;
        }
      }
    }
  });

  // Put the image data back
  ctx.putImageData(imageData, 0, 0);

  // Draw grid only if pixel size is large enough
  // if (pixelSize >= 4) {
  //   ctx.strokeStyle = '#eee'
  //   ctx.lineWidth = 1

  //   // Draw grid in a single path
  //   ctx.beginPath()

  //   // Vertical lines
  //   for (let x = 0; x <= width * pixelSize; x += pixelSize) {
  //     ctx.moveTo(x, 0)
  //     ctx.lineTo(x, height * pixelSize)
  //   }

  //   // Horizontal lines
  //   for (let y = 0; y <= height * pixelSize; y += pixelSize) {
  //     ctx.moveTo(0, y)
  //     ctx.lineTo(width * pixelSize, y)
  //   }

  //   ctx.stroke()
  // }
}

export function exportToPng(
  width: number,
  height: number,
  pixels: Uint32Array
): string | null {
  // const pixelSize = getPixelSizeForCanvas(width);
  const { createTempCanvas } = useImageProcessing();
  const tempCanvas = createTempCanvas(CANVAS_SIZE, CANVAS_SIZE);
  const tempCtx = tempCanvas.getContext("2d", {
    alpha: true,
    willReadFrequently: true,
  });

  if (!tempCtx) return null;
  tempCtx.imageSmoothingEnabled = true;

  // // Clear the canvas to be fully transparent
  tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);

  pixels.forEach((color, index) => {
    if (color === 0) return;
    const x = index % width;
    const y = Math.floor(index / width);

    const { r, g, b } = intToRgb(color);

    tempCtx.fillStyle = `rgba(${r},${g},${b},1)`;
    tempCtx.fillRect(x, y, 1, 1);
  });

  return tempCanvas.toDataURL("image/png");
}
